import { formatPostalCode, type ClientJob } from '@hemmafixa/shared';
import {
  Button,
  Card,
  DescriptionList,
  DetailLayout,
  DetailSection,
  EmptyState,
  FormField,
  PageHeader,
  StatusBadge,
  Textarea,
  toast,
} from '@hemmafixa/ui';
import { MessageCircleQuestion, SearchX } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { useI18n } from '@/i18n/context';
import { BackLink } from '../shared/back-link';
import { useCategoryLabel } from '../shared/catalogue';
import { JobEditForm } from '../shared/job-edit-form';
import { JobLocationMap } from '../shared/job-location-map';
import { JobFilesEditor } from '../shared/job-files';
import { JobQuestionList } from '../shared/job-questions';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { useAnswerMyJob, useEditMyJob, useJobAttachments, useMyJob } from './api';
import { CloseJobPanel } from './close-job-panel';
import { JobProposals } from './job-proposals';

/** One of the client's jobs: answer Hemmafixa's questions, edit it while in review, see its status (P3-09). */
export function ClientJobPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  return (
    <div className="grid gap-4">
      <BackLink to="/client/jobs" label={t.nav.myJobs} />
      <ClientJobDetail id={id} />
    </div>
  );
}

/** Loads and shows one of the client's jobs; the full page and My jobs' details panel both use it. */
export function ClientJobDetail({ id }: { id: string }) {
  const { t } = useI18n();
  const query = useMyJob(id);
  return (
    <QueryView query={query} loading="detail">
      {(job) =>
        !job ? (
          <EmptyState icon={SearchX} title={t.common.notFoundTitle} description={t.common.notFoundText} />
        ) : (
          <ClientJobView job={job} />
        )
      }
    </QueryView>
  );
}

function ClientJobView({ job }: { job: ClientJob }) {
  const { t, date } = useI18n();
  const statuses = useStatuses();
  const categoryLabel = useCategoryLabel();
  const edit = useEditMyJob(job.id);
  const attachments = useJobAttachments(job.id);
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState(false);
  const closable = job.status === 'open' || job.status === 'in_review' || job.status === 'needs_info';
  const c = t.client.job;
  const openQuestions = job.questions.filter(
    (question) => question.source === 'admin' && question.answer === null,
  );
  const published = job.status === 'open' || job.status === 'provider_chosen';

  return (
    <>
      <PageHeader
        title={job.title}
        description={categoryLabel(job.categorySlug)}
        actions={
          job.editable &&
          !editing && (
            <Button variant="outline" onClick={() => setEditing(true)}>
              {c.edit}
            </Button>
          )
        }
      />
      <DetailLayout
        aside={
          <DetailSection title={t.job.details}>
            <div className="grid gap-4">
              <StatusBadge status={job.status} statuses={statuses.job} className="justify-self-start" />
              {job.status === 'in_review' && (
                <p className="text-sm text-muted-foreground">{c.inReviewText}</p>
              )}
              <DescriptionList
                items={[
                  { label: t.job.category, value: categoryLabel(job.categorySlug) },
                  { label: t.job.location, value: formatPostalCode(job.postalCode) },
                  { label: t.job.wantedStart, value: t.wantedStart[job.wantedStart] },
                  ...(job.submittedAt ? [{ label: t.job.posted, value: date(job.submittedAt) }] : []),
                ]}
              />
              {closable &&
                (closing ? (
                  <CloseJobPanel jobId={job.id} onClose={() => setClosing(false)} />
                ) : (
                  <Button variant="outline" onClick={() => setClosing(true)}>
                    {c.closeJob}
                  </Button>
                ))}
            </div>
          </DetailSection>
        }
      >
        <JobLocationMap location={job.location} postalCode={job.postalCode} className="h-44" />
        {job.status === 'needs_info' && openQuestions.length > 0 && (
          <AnswerForm jobId={job.id} questions={openQuestions} />
        )}
        {job.status === 'rejected' && job.rejectionReason && (
          <Card className="grid gap-1 border-danger/40 p-5">
            <h2 className="font-semibold">{c.rejectedTitle}</h2>
            <p className="whitespace-pre-line text-sm">{job.rejectionReason}</p>
          </Card>
        )}
        {published && <JobProposals jobId={job.id} />}
        {editing ? (
          <JobEditForm job={job} description={c.editText} mutation={edit} onClose={() => setEditing(false)} />
        ) : (
          <DetailSection title={t.job.description}>
            <p className="whitespace-pre-line text-sm leading-relaxed">{job.description}</p>
          </DetailSection>
        )}
        <DetailSection title={c.questions}>
          <JobQuestionList questions={job.questions} />
        </DetailSection>
        <DetailSection title={t.files.title}>
          <JobFilesEditor files={job.attachments} editable={job.editable} {...attachments} />
          {!job.editable && job.attachments.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">{t.files.locked}</p>
          )}
        </DetailSection>
      </DetailLayout>
    </>
  );
}

/** Hemmafixa's open questions. Sending every answer puts the job back in the review queue. */
function AnswerForm({ jobId, questions }: { jobId: string; questions: ClientJob['questions'] }) {
  const { t } = useI18n();
  const c = t.client.job;
  const answer = useAnswerMyJob(jobId);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const missing = Object.fromEntries(
      questions.filter((q) => !(answers[q.id] ?? '').trim()).map((q) => [q.id, c.answerRequired]),
    );
    setErrors(missing);
    if (Object.keys(missing).length > 0) return;
    answer.mutate(
      questions.map((q) => ({ questionId: q.id, answer: answers[q.id]!.trim() })),
      {
        onSuccess: () => toast.success(c.answeredToast),
        onError: () => toast.error(c.answerFailed),
      },
    );
  };

  return (
    <Card className="border-primary/50 p-5 sm:p-6">
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <div className="flex items-start gap-3">
          <MessageCircleQuestion className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
          <div>
            <h2 className="font-semibold">{c.questionsTitle}</h2>
            <p className="text-sm text-muted-foreground">{c.questionsText}</p>
          </div>
        </div>
        {questions.map((question) => (
          <FormField key={question.id} label={question.question} error={errors[question.id]} required>
            <Textarea
              rows={3}
              maxLength={500}
              placeholder={c.yourAnswer}
              value={answers[question.id] ?? ''}
              onChange={(event) =>
                setAnswers((current) => ({ ...current, [question.id]: event.target.value }))
              }
            />
          </FormField>
        ))}
        <Button type="submit" className="justify-self-start" loading={answer.isPending}>
          {c.sendAnswers}
        </Button>
      </form>
    </Card>
  );
}
